import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Camera, X, ArrowCounterClockwise, Check } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';

// ライブカメラ撮影ダイアログ — getUserMedia でカメラを起動し、その場で撮影。
// onCapture(file: File) を呼ぶ。カメラ不可の環境ではエラートーストを出して閉じる。
export const CameraCaptureDialog = ({ open, onClose, onCapture }) => {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [shot, setShot] = useState(null); // {blob, url}
  const [starting, setStarting] = useState(true);
  const [facing, setFacing] = useState('environment');

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks()?.forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startStream = useCallback(async (mode) => {
    stopStream();
    setStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: mode, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStarting(false);
    } catch (e) {
      toast.error('カメラを起動できませんでした。ブラウザのカメラ許可を確認するか、「ファイル選択」をご利用ください');
      onClose();
    }
  }, [stopStream, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    setShot(null);
    startStream(facing);
    return () => { stopStream(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, facing]);

  const capture = () => {
    const video = videoRef.current;
    if (!video || !video.videoWidth) return;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext('2d').drawImage(video, 0, 0);
    canvas.toBlob((blob) => {
      if (!blob) { toast.error('撮影に失敗しました'); return; }
      setShot({ blob, url: URL.createObjectURL(blob) });
      stopStream();
    }, 'image/jpeg', 0.85);
  };

  const use = () => {
    if (!shot) return;
    const file = new File([shot.blob], `camera_${Date.now()}.jpg`, { type: 'image/jpeg' });
    URL.revokeObjectURL(shot.url);
    onCapture(file);
    onClose();
  };

  const retake = () => {
    if (shot) URL.revokeObjectURL(shot.url);
    setShot(null);
    startStream(facing);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg p-0 overflow-hidden bg-zinc-950 border-zinc-800" aria-describedby={undefined} data-testid="eng-camera-dialog">
        <DialogTitle className="sr-only">カメラで撮影</DialogTitle>
        <div className="relative aspect-[4/3] bg-black flex items-center justify-center">
          {shot ? (
            <img src={shot.url} alt="撮影プレビュー" className="w-full h-full object-contain" data-testid="eng-camera-preview" />
          ) : (
            <video ref={videoRef} playsInline muted className="w-full h-full object-cover" data-testid="eng-camera-video" />
          )}
          {starting && !shot && <p className="absolute text-white/70 text-sm">カメラ起動中…</p>}
          <button onClick={onClose} className="absolute top-3 right-3 bg-black/50 text-white rounded-full p-2 backdrop-blur" data-testid="eng-camera-close">
            <X size={18} weight="bold" />
          </button>
        </div>
        <div className="flex items-center justify-center gap-3 p-4 bg-zinc-950">
          {shot ? (
            <>
              <Button variant="outline" onClick={retake} className="h-12 px-5 border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800" data-testid="eng-camera-retake">
                <ArrowCounterClockwise size={18} className="mr-1.5" /> 撮り直す
              </Button>
              <Button onClick={use} className="h-12 px-8 bg-teal-500 hover:bg-teal-400 text-white text-base" data-testid="eng-camera-use">
                <Check size={18} weight="bold" className="mr-1.5" /> この写真を使う
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setFacing((f) => (f === 'environment' ? 'user' : 'environment'))}
                className="h-12 px-4 border-zinc-700 bg-zinc-900 text-zinc-200 hover:bg-zinc-800" data-testid="eng-camera-flip">
                <ArrowCounterClockwise size={16} className="mr-1" /> 切替
              </Button>
              <button onClick={capture} disabled={starting}
                className="w-16 h-16 rounded-full bg-white ring-4 ring-white/30 hover:ring-white/50 active:scale-95 transition-all disabled:opacity-40 flex items-center justify-center"
                data-testid="eng-camera-shutter" aria-label="撮影">
                <Camera size={26} weight="fill" className="text-zinc-900" />
              </button>
              <div className="w-[92px]" />
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
